import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AppConfig } from '@solar/app.config';
import { Site } from 'solar-bo';
import { Observable, of, BehaviorSubject, from } from 'rxjs';
import { delay, tap, catchError, mergeMap } from 'rxjs/operators';
import { shareReplay, share, switchMap, map } from 'rxjs/operators';
import * as uuid from 'uuid';

import { FilterDTO } from 'base-models';
import { ErrorcontrolService } from 'basecomponents';
import { Supervision, SupervisionError, InfoIssue, Panel, IssueImage } from 'solar-bo';;
import { Flight } from 'solar-bo';
import { SdsService, ISdsAction } from '@solar/services/sds.service';
import { SolarCacheService } from './solarcache.service';
import { PanelType } from 'solar-bo';



@Injectable({
    providedIn: 'root'
})
export class SolarService {

    localdebug: boolean = false;
    private apiurlbase = AppConfig.API_ENDPOINT + '';  // URL to web api

    constructor(
        private http: HttpClient
        , private errorcontrolservice: ErrorcontrolService
        , private sdsService: SdsService
        , public solarCacheSvc: SolarCacheService) {

        //console.log("retrieving initial data");

        this.loadInitialData();

        sdsService.actions.subscribe((action) => this.sdsServiceActionsHandler(action));
    }


    sdsServiceActionsHandler(action: ISdsAction) {
        if (action) {
            if (action.action == "LOGOUT") {
                this.isloadingInitialData = false
                this.solarCacheSvc.sites$.next(null);
                this.isloadingInitialData = false
            }
        }
    }
    /**
     * return sites array
     */
    get sites() {
        return this.solarCacheSvc.sites;
    }
    /**
     * return a site from cache
     */
    siteById(id: string) {
        return this.solarCacheSvc.siteById(id);
    }

    isloadingInitialData: boolean = false;
    async loadInitialData() {
        if (this.isloadingInitialData == true) {
            return;
        }
        else {
            this.isloadingInitialData = false;
        }
        this.sitesGetAll()
            .subscribe(
                res => {
                    this.isloadingInitialData = false;
                },
                err => console.log("Error retrieving initial data")
            )

    }

    refreshSiteDatainterval: any;
    configureRefreshSiteData() {

        if (this.refreshSiteDatainterval) {
            clearInterval(this.refreshSiteDatainterval);
        }
        this.refreshSiteDatainterval = setInterval(() => {

        }, 10000);
    }

    aquery(query: string): Observable<any> {
        //console.log(JSON.stringify(entity));
        const url = `${this.apiurlbase}/query`;
        console.log(query);
        return this.http.post<any>(url, query, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`query result`)),
            catchError(this.errorcontrolservice.handleError<any>('aquery:' + query))
        );
    }


    companyInfo(company_id: string, filter?: string): Observable<any> {
        let url = this.apiurlbase + "/companies/" + company_id;

        return this.http.get<any[]>(url, AppConfig.httpOptions)
            .pipe(
                catchError(this.errorcontrolservice.handleError<Site>(`companyInfo id=${company_id}`))
            );
    }
    /**
     * Return all sites for a company
     * @param company_id 
     * @param filter 
     */
    companySitesGetAll(company_id: string, filter?: string): Observable<Site[]> {
        let url = this.apiurlbase + "/companies/" + company_id + "/sites";
        if (filter) {
            url = url + "?" + filter;
        }

        return this.http.get<any[]>(url, AppConfig.httpOptions)
            .pipe(
                catchError(this.errorcontrolservice.handleError('companySitesGetAll', []))
                , share()
            );
    }
    /**
     * return all sites and cache in site list
     * @param filter 
     */
    sitesGetAll(filter?: FilterDTO): Observable<Site[]> {
        let url = this.apiurlbase + "/sites";

        if (this.sites && this.sites.length > 0) {
            console.log("retrieving cached sites")
            return this.solarCacheSvc.sites$;
        }
        return this.http.get<any[]>(url, AppConfig.httpOptions)
            .pipe(
                tap(res => {
                    this.solarCacheSvc.sites$.next(res);
                    this.errorcontrolservice.log(`sitesGetAll ok`);
                }),
                catchError(this.errorcontrolservice.handleError('sitesGetAll', []))
                , share()
            );
    }
    /**
     * Return sites filtered
     * @param filter 
     */
    sitesGetByFilter(filter: FilterDTO): Observable<Site[]> {
        let jsonfilter = encodeURIComponent(JSON.stringify(filter));
        let url = this.apiurlbase + "/sites?jsonfilter=" + jsonfilter;

        return this.http.get<Site[]>(url, AppConfig.httpOptions)
            .pipe(
                tap(res => {

                }),
                catchError(this.errorcontrolservice.handleError('sitesGetByFilter', []))
            );
    }


    /**
     * Get site by id
     * @param id 
     */
    siteGetById(id: string, forceRefresh: boolean = false): Observable<Site> {
        const url = `${this.apiurlbase}/sites/${id}`;
        let site = null;
        if (!forceRefresh) {
            site = this.siteById(id);
            if (site) {
                return of(site);
            }
            else {
                return this.http.get<Site>(url).pipe(
                    tap((ent: Site) => {
                        this.solarCacheSvc.siteListAddedorEdited(ent),
                        this.errorcontrolservice.log(`get site  w/ id=${id}`)
                    }),
                    catchError(this.errorcontrolservice.handleError<Site>(`siteGetById id=${id}`))
                );
            }
        }
        else {
            return this.http.get<Site>(url).pipe(
                tap((ent: Site) => {                    
                    this.errorcontrolservice.log(`get site  w/ id=${id}`)
                })
                ,map(ent => this.solarCacheSvc.siteListAddedorEdited(ent) )
                ,catchError(this.errorcontrolservice.handleError<Site>(`siteGetById id=${id}`))
            );
        }
    }

    siteGetSupervisions(id: string): Observable<Supervision[]> {
        const url = `${this.apiurlbase}/sites/${id}/supervisions`;
        return this.http.get<Supervision>(url).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched siteGetSupervisions id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`siteGetSupervisions id=${id}`))
        );
    }


    siteEditCreate(entity: Site): Observable<Site> {
        console.log(JSON.stringify(entity));
        const url = `${this.apiurlbase}/sites`;
        return this.http.post<Site>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: Site) => {
                this.solarCacheSvc.siteListAddedorEdited(ent),
                    this.errorcontrolservice.log(`edit site  w/ id=${entity.id}`)
            }), catchError(this.errorcontrolservice.handleError<Site>('siteeditorcreate'))
        );
    }


    siteDelete(entity: Site | string): Observable<Site> {
        const id = typeof entity === 'string' ? entity : entity.id;
        const url = `${this.apiurlbase}/sites/${id}`;

        return this.http.delete<Site>(url, AppConfig.httpOptions).pipe(
            tap((ent: Site) => {
                this.solarCacheSvc.siteListDelete(id),
                    this.errorcontrolservice.log(`siteDelete w/ id=${id}`)
            }),
            catchError(this.errorcontrolservice.handleError<Site>('siteDelete'))
        );
    }



    /**
     * Return site panels with all info
     * @param id_site 
     */
    async siteGetPanels(id_site: string, limit = -1, offset = -1): Promise<any> {

        const panels = await this.solarCacheSvc.sitePanelsByIdAsync(id_site);

        if (panels && panels.length > 0) {
            return Promise.resolve(panels);
        }
        else {
            let url = `${this.apiurlbase}/sites/${id_site}/panels`;
            url += "?filter={\"columns\":[\"panel.id\",\"locator\", \"id_panel_type\",\"geom\"]";
            if (limit != -1) {
                url += ',\"limit\":' + limit;
            }
            if (offset != -1) {
                url += ',\"offset\":' + offset;
            }
            url += "}";//close

            return this.http.get<any[]>(url).pipe(
                tap(res => {
                    this.solarCacheSvc.sitePanelsAddedorEdited(id_site, res);
                    this.errorcontrolservice.log(`fetched siteGetPanels id=${id_site}`);
                }),
                catchError(this.errorcontrolservice.handleError<any>(`siteGetPanels id=${id_site}`))
            ).toPromise();
        }

    }

    /**
     * Return site panels with all info
     * IMPORTANT: this method dont save to cache, it must be done by caller at the end
     * @param id_site 
     */
    async siteGetPanelsByLoop(id_site: string, limit = -1, offset = -1): Promise<any> {

        const panels = await this.solarCacheSvc.sitePanelsByIdAsync(id_site);

        if (panels && panels.length > 0) {
            return Promise.resolve(panels);
        }
        else {
            let url = `${this.apiurlbase}/sites/${id_site}/panels`;
            url += "?filter={\"columns\":[\"panel.id\",\"locator\", \"id_panel_type\",\"geom\"]";
            if (limit != -1) {
                url += ',\"limit\":' + limit;
            }
            if (offset != -1) {
                url += ',\"offset\":' + offset;
            }
            url += "}";//close

            return this.http.get<any[]>(url).pipe(
                tap(res => {
                    this.errorcontrolservice.log(`fetched siteGetPanels id=${id_site}`);
                }),
                catchError(this.errorcontrolservice.handleError<any>(`siteGetPanels id=${id_site}`))
            ).toPromise();
        }

    }

    /**
     * return site panels types(model/brand, etc)
     * @param id_site 
     * @param filter ?filter={"columns":["brand", "model"]}
     */
    siteGetPanelsTypes(id_site: string, filter?: string): Promise<any[]> {
        let url = `${this.apiurlbase}/sites/${id_site}/panels/type`;
        /*         if (filter){
                    url + "?" + filter;
                } */
        return this.http.get<any[]>(url).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched siteGetPanelsTypes id=${id_site}`)),
            catchError(this.errorcontrolservice.handleError<any>(`siteGetPanelsTypes id=${id_site}`))
        ).toPromise();
    }

    sitePanelTypeEditCreate(entity: PanelType): Observable<PanelType> {
        console.log(JSON.stringify(entity));
        const url = `${this.apiurlbase}/paneltypes`;
        return this.http.post<any>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: any) => {
                this.errorcontrolservice.log(`edit site  w/ id=${entity.id}`)
            }), catchError(this.errorcontrolservice.handleError<Site>('siteeditorcreate'))
        );
    }

    sitePanelsTypesDeleteById(id: string): Observable<any> {
        const url = `${this.apiurlbase}/panels/${id}`;
        return this.http.delete<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched sitePanelsTypesDeleteById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`sitePanelsTypesDeleteById id=${id}`))
        );
    }

    siteGetFilesById(id: string): Observable<any[]> {
        const url = `${this.apiurlbase}/sites/${id}/files`;
        return this.http.get<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched siteGetFilesById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`siteGetFilesById id=${id}`))
        );
    }
    /**
 * Return a panel with selected locator
 * locatorAsString as L1=5&L2=3A&L3=XXX etc
 * @param id_site 
 * @param locatorAsString 
 */
    async siteGetPanelByLocator(id_site: string, locatorAsString: string): Promise<any> {
        //locatorAsString=encodeURIComponent(locatorAsString);
        const url = `${this.apiurlbase}/sites/${id_site}/panels/locator?${locatorAsString}`;
        //console.log("siteGetPanelByLocator_0 ",url);
        return this.http.get<any>(url).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched siteGetPanelByLocator id=${id_site} ${locatorAsString}`)),
            catchError(this.errorcontrolservice.handleError<any>(`siteGetPanelByLocator id=${id_site} ${locatorAsString}`))
        ).toPromise();
    }
    /**
     * Get panel data 
     * @param id_panel 
     */
    async siteGetPanelById(id_panel: string): Promise<any> {
        const url = `${this.apiurlbase}/panels/${id_panel}`;
        return this.http.get<any[]>(url).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched siteGetPanelById id=${id_panel}`)),
            catchError(this.errorcontrolservice.handleError<any>(`siteGetPanelById id=${id_panel}`))
        ).toPromise();
    }


    /**
     * Upload file with panels to server
     * @param entity 
     * @param panels_formdata 
     * @returns 
     */
     siteUploadPanels(entity: Site | string, panels_formdata:any){
        const id = typeof entity === 'string' ? entity : entity.id;
        const url = `${this.apiurlbase}/sites/${id}/panels`;
       
        return this.http.post<any>(url, panels_formdata).pipe(
            tap((response: Site) => {        
            }), catchError(this.errorcontrolservice.handleError<Site>('siteUploadPanels'))
        );
    }
    /**
     * upload sync file to server
     * @param entity site or id_site
     * @param formdata 
     * @returns 
     */
    siteUploadFile(entity: Site | string, formdata:any){
        const id = typeof entity === 'string' ? entity : entity.id;
        const url = `${this.apiurlbase}/sites/${id}/files`;
       
        return this.http.post<any>(url, formdata).pipe(
            tap((response: Site) => {        
            }), catchError(this.errorcontrolservice.handleError<Site>('siteUploadFile'))
        );
    }


    deleteResourceById(id: string): Observable<any> {
        const url = `${this.apiurlbase}/uploads/${id}`;
        return this.http.delete<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched DeleteResourceById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`DeleteResourceById id=${id}`))
        );
    }

    supervisionsGetByFilter(filter?: FilterDTO): Observable<any[]> {
        let jsonfilter = encodeURIComponent(JSON.stringify(filter));

        let u = this.apiurlbase + "/supervisions";
        //let u = this.apiurlbase + "/supervisions/listbyfilter?jsonfilter=" + jsonfilter;
        //url += "?filter={\"columns\":[\"panel.id\",\"locator\", \"id_panel_type\",\"geom\"]";

        return this.http.get<any[]>(u, AppConfig.httpOptions)
            .pipe(
                tap(contract => this.errorcontrolservice.log(`fetched supervisionsGetByFilter ok`)),
                catchError(this.errorcontrolservice.handleError('supervisionsGetByFilter', []))
            );
    }

    supervisionEditCreate(entity: Supervision): Observable<Supervision> {
        const url = `${this.apiurlbase}/supervisions`;
        return this.http.post<Supervision>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: Supervision) => this.errorcontrolservice.log(`edit Supervision  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('supervisionEditCreate'))
        );
    }
    /**
     * simple update
     * @param entity 
     */
    supervisionUpdateSimple(entity: any): Observable<Supervision> {
        const url = `${this.apiurlbase}/supervisions`;
        return this.http.post<Supervision>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: Supervision) => this.errorcontrolservice.log(`edit Supervision  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('supervisionEditCreate'))
        );
    }

    supervisionGetById(id: string): Observable<Supervision> {
        const url = `${this.apiurlbase}/supervisions/${id}`;
        return this.http.get<Supervision>(url).pipe(
            /* tap(_ => this.errorcontrolservice.log(`fetched supervisionGetById id=${id}`)), */
            catchError(this.errorcontrolservice.handleError<any>(`supervisionGetById id=${id}`))
        );
    }

    supervisionDeleteById(id: string): Observable<Flight> {
        const url = `${this.apiurlbase}/supervisions/${id}`;
        return this.http.delete<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched supervisionDeleteById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`supervisionDeleteById id=${id}`))
        );
    }

    supervisionGetIssues(id: string, filter: string = ""): Observable<SupervisionError[]> {
        let url = `${this.apiurlbase}/supervisions/${id}/issues`;
        if (filter) {
            url = url + "?" + filter;
        }
        return this.http.get<SupervisionError[]>(url).pipe(
            map(respdata => {
                for (let i = 0; i < respdata.length; i++) {
                    let item: SupervisionError = Object.assign(new SupervisionError(), respdata[i]);
                    item.info = Object.assign(new InfoIssue(), item.info);
                    respdata[i] = item;

                }
                return respdata;
            }),
            tap(_ => this.errorcontrolservice.log(`fetched supervisionGetIssues id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`supervisionGetIssues id=${id}`))
        );
    }
    /**
     * 
     * @param id used in dashboard, in future must use supervisionGetIssues
     */
    supervisionGetResults(id: string): Observable<any[]> {
        const url = `${this.apiurlbase}/supervisions/${id}/results`;
        return this.http.get<any[]>(url).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched supervisionGetIssues id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`supervisionGetIssues id=${id}`))
        );
    }

    supervisioGetFilesById(id: string): Observable<any[]> {
        const url = `${this.apiurlbase}/supervisions/${id}/files`;
        return this.http.get<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched supervisioGetFilesById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`supervisioGetFilesById id=${id}`))
        );
    }

    supervisionGetPanels(id_site: string): Observable<any[]> {
        const url = `${this.apiurlbase}/sites/${id_site}/panels`;
        return this.http.get<any[]>(url).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched supervisionGetPanels id=${id_site}`)),
            catchError(this.errorcontrolservice.handleError<any>(`supervisionGetPanels id=${id_site}`))
        );
    }
    /**
     * Return all panels asociated with issues in supervision
     * @param id_supervision 
     */
    supervisionGetPanelsWithError(id_supervision: string): Promise<any> {
        let url = `${this.apiurlbase}/supervisions/${id_supervision}/panels_error`;
        //url += "?filter={\"columns\":[\"panel.id\",\"locator\", \"id_panel_type\",\"geom\"]}";
        return this.http.get<any[]>(url).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched supervisionGetPanelsWithError id=${id_supervision}`)),
            catchError(this.errorcontrolservice.handleError<any>(`supervisionGetPanelsWithError id=${id_supervision}`))
        ).toPromise();
    }

    supervisionIssueUpdate(entity: SupervisionError): Observable<SupervisionError> {
        const url = `${this.apiurlbase}/issues`;
        return this.http.post<any>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: SupervisionError) => this.errorcontrolservice.log(`edit supervisionIssueUpdate  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('supervisionIssueUpdate'))
        );
    }

    supervisionIssueFind(entity: SupervisionError): Observable<SupervisionError[]> {
        //const url = `${this.apiurlbase}/issues/find`;
        //return of(null)
        //.pipe(delay(5000));

        const url = `${this.apiurlbase}/issues/new`;
        return this.http.post<any>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: SupervisionError[]) => this.errorcontrolservice.log(`supervisionIssueFind  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('supervisionIssueFind'))
        );
    }

    /**
     * adjust image RGB
     * @param id_image 
     * @param data 
     * @returns 
     */
         image_rgb_adjust(id_image:string, data:{low:number,high:number}): Observable<any> {
            const url = `${this.apiurlbase}/images/${id_image}/rgb_adjust`;
            //const url = `https://prepro-esolar.geoaitech.com/api/images/${id_image}/rgb_adjust`;
            return this.http.post<any>(url, data, AppConfig.httpOptions).pipe(
                catchError(this.errorcontrolservice.handleError<any>('supervisionIssueFind'))
            );
        }

    supervisionGetFlights(id: string): Observable<any[]> {
        const url = `${this.apiurlbase}/supervisions/${id}/flights`;
        return this.http.get<any[]>(url).pipe(
            /* tap(_ => this.errorcontrolservice.log(`fetched supervisionGetFlights id=${id}`)), */
            catchError(this.errorcontrolservice.handleError<any>(`supervisionGetFlights id=${id}`))
        );
    }


    supervisionProcessFlights(id: string): Observable<any> {
        const url = `${this.apiurlbase}/supervisions/${id}/process`;
        return this.http.post<any>(url, id, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`supervisionProcessFlights  w/ id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>('supervisionProcessFlights'))
        );
    }
    supervisionFindIssues(id: string): Observable<any> {
        const url = `${this.apiurlbase}/supervisions/${id}/find_issues`;
        return this.http.post<any>(url, id, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`supervisionFindIssues  w/ id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>('supervisionFindIssues'))
        );
    }

    supervisionImageChangePanelOffset(id_supervision: string, change: { id_image: string, id_supervision: string, offset_x: number, offset_y: number }): Observable<any> {
        const url = `${this.apiurlbase}/supervisions/${id_supervision}/changeimagepanelsoffset`;

        /*         let tst=[];
                tst.push({id_issue:"8a09d834-bf7c-11ea-91d6-509a4c837ab7",id_panel:"52947710-a979-11ea-a41a-509a4c837ab7"});
                tst.push({id_issue:"8a09eaa4-bf7c-11ea-91d6-509a4c837ab7",id_panel:"52948a7a-a979-11ea-a41a-509a4c837ab7"});
                return of (tst); */


        return this.http.post<any>(url, change, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`supervisionImageChangePanelOffset  w/ id=${id_supervision}`)),
            catchError(this.errorcontrolservice.handleError<any>('supervisionImageChangePanelOffset'))
        );
    }
    supervisionExportIssuesToExcel(id_supervision: string) {
        const url = `${this.apiurlbase}/supervisions/${id_supervision}/export/excel`;
        return this.http.post<any>(url, null, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`exportIssuesToExcel  w/ id=${id_supervision}`)),
            catchError(this.errorcontrolservice.handleError<any>('exportIssuesToExcel'))
        );
    }
    supervisionExportToPDF(id_supervision: string) {
        const url = `${this.apiurlbase}/superivisions/${id_supervision}/export/pdf`;
        return this.http.post<any>(url, null, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`exportIssuesToExcel  w/ id=${id_supervision}`)),
            catchError(this.errorcontrolservice.handleError<any>('exportIssuesToExcel'))
        );
    }

    supervisionExportResults(id_supervision: string) {
        const url = `${this.apiurlbase}/superivisions/${id_supervision}/export/results`;
        return this.http.post<any>(url, null, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`exportIssuesToExcel  w/ id=${id_supervision}`)),
            catchError(this.errorcontrolservice.handleError<any>('exportIssuesToExcel'))
        );
    }

    flightEditCreate(entity: Flight): Observable<Flight> {
        const url = `${this.apiurlbase}/flights`;
        return this.http.post<Flight>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`edit Flight  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('flightEditCreate'))
        );
    }

    flightGetById(id: string): Observable<Flight> {
        const url = `${this.apiurlbase}/flights/${id}`;
        return this.http.get<Flight>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched flightGetById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`flightGetById id=${id}`))
        );
    }
    flightDeleteById(id: string): Observable<Flight> {
        const url = `${this.apiurlbase}/flights/${id}`;
        return this.http.delete<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched flightDeleteById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`flightDeleteById id=${id}`))
        );
    }

    flightFilesDeleteById(id: string): Observable<Flight> {
        const url = `${this.apiurlbase}/flights/${id}/files/delete`;
        const body = { delete: "ALL", flight_id: id };
        return this.http.post<any>(url, body, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`flightFilesDeleteById`)),
            catchError(this.errorcontrolservice.handleError<any>('flightFilesDeleteById'))
        );
    }

    flightGetImagesById(id: string): Observable<any[]> {
        const url = `${this.apiurlbase}/flights/${id}/images`;
        return this.http.get<any[]>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched flightGetImagesById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`flightGetImagesById id=${id}`))
        );
    }

    flightGetImagesCaroussel(id: string): Observable<any[]> {
        let url = `${this.apiurlbase}/flights/${id}/images`;
        //url += "?filter={\"columns\":[\"panel.id\",\"locator\", \"id_panel_type\",\"geom\"]}";
        url += "?filter={\"columns\":[\"supervision_image.id\",\"supervision_image.name\",\"supervision_image.time\",\"supervision_image.geom_polygon\",\"supervision_image.geom_shot\"], \"filter\": \"selected=true\",\"orderby\":\"supervision_image.time \"}";
        return this.http.get<any[]>(url, AppConfig.httpOptions).pipe(
            /* tap(_ => this.errorcontrolservice.log(`fetched flightGetImagesById id=${id}`)), */
            catchError(this.errorcontrolservice.handleError<any>(`flightGetImagesById id=${id}`))
        );
    }

    flightGetFilesById(id: string): Observable<any[]> {
        const url = `${this.apiurlbase}/flights/${id}/files`;

        return this.http.get<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched flightGetFilesById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`flightGetFilesById id=${id}`))
        );
    }

    flightDeleteResourceById(id: string): Observable<Flight> {
        const url = `${this.apiurlbase}/flights/${id}/files`;
        return this.http.delete<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched flightDeleteResourceById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`flightDeleteResourceById id=${id}`))
        );
    }
    /**
     * Process flight images uploaded
     * @param entity 
     */
    flightProcess(entity: Flight): Observable<Flight> {
        const url = `${this.apiurlbase}/flights/${entity.id}/process`;
        return this.http.post<any>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`flightProcess  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('flightProcess'))
        );
    }
    /**
     * Find issues on flight
     * @param entity 
     */
    flightFindIssues(entity: Flight): Observable<Flight> {
        const url = `${this.apiurlbase}/flights/${entity.id}/find_issues`;
        return this.http.post<any>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`flightProcess  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('flightProcess'))
        );
    }
    /**
     * Project images of flight
     * @param entity 
     */
    flightProjectImages(entity: Flight): Observable<Flight> {
        const url = `${this.apiurlbase}/flights/${entity.id}/project`;
        return this.http.post<any>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: any) => this.errorcontrolservice.log(`flightProjectImages  w/ id=${entity.id}`)),
            catchError(this.errorcontrolservice.handleError<any>('flightProjectImages'))
        );
    }
    /**
     * Return image data
     * @param id_image 
     * @param filter 
     */
    FlightImagesGetById(id_image: string, filter: string = ""): Observable<any> {
        let url = `${this.apiurlbase}/images/${id_image}`;
        if (filter) {
            //example url += "?filter={\"columns\":[\"panel.id\",\"locator\", \"id_panel_type\",\"geom\"]";
            url += "?filter=" + filter;
            //el servidor no tiene filtro asi que no te molestes 
        }

        return this.http.get<IssueImage>(url, AppConfig.httpOptions).pipe(
            /* tap(_ => this.errorcontrolservice.log(`fetched FlightImagesGetById id=${id_image}`)), */
            catchError(this.errorcontrolservice.handleError<any>(`FlightImagesGetById id=${id_image}`))
        );
    }

    PanelsTypesList(filter?: string): Observable<any[]> {
        let url = `${this.apiurlbase}/paneltypes`;
        /*         if (filter){
                    url + "?" + filter;
                } */
        return this.http.get<any[]>(url).pipe(
            /* tap(_ => this.errorcontrolservice.log(`fetched siteGetPanelsTypes id=${id_site}`)), */
            catchError(this.errorcontrolservice.handleError<any>(`PanelsTypesList `))
        );
    }
    panelsTypeById(id: string): Observable<any> {
        const url = `${this.apiurlbase}/paneltypes/${id}`;

        return this.http.get<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched panelsTypeById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`panelsTypeById id=${id}`))
        );
    }

    PanelTypeEditCreate(entity: PanelType | PanelType[]): Observable<PanelType> {
        console.log(JSON.stringify(entity));
        const url = `${this.apiurlbase}/paneltypes`;
        return this.http.post<any>(url, entity, AppConfig.httpOptions).pipe(
            tap((ent: any) => {
                this.errorcontrolservice.log(`PanelTypeEditCreate`)
            }), catchError(this.errorcontrolservice.handleError<Site>('PanelTypeEditCreate'))
        );
    }

    PanelsTypesDeleteById(id: string): Observable<any> {
        const url = `${this.apiurlbase}/paneltypes/${id}`;
        return this.http.delete<any>(url, AppConfig.httpOptions).pipe(
            tap(_ => this.errorcontrolservice.log(`fetched PanelsTypesDeleteById id=${id}`)),
            catchError(this.errorcontrolservice.handleError<any>(`PanelsTypesDeleteById id=${id}`))
        );
    }

}